"use client"

import { useEffect } from "react"
import { ErrorPageTemplate, ErrorTemplates } from "@/components/error-page-template"
import { Button } from "@/components/ui/button"
import { RefreshCw, Bug } from "lucide-react"

interface ErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error)
  }, [error])

  const customActions = (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        <p className="font-medium mb-2">What you can try:</p>
        <ul className="text-left space-y-1">
          <li>• Refresh the page to try again</li>
          <li>• Check your internet connection</li>
          <li>• Clear your browser cache</li>
          <li>• Try again in a few minutes</li>
        </ul>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button onClick={reset} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Try Again
        </Button>
        
        {process.env.NODE_ENV === 'development' && (
          <Button 
            variant="outline" 
            onClick={() => console.log('Error details:', error)}
            className="flex items-center gap-2"
          >
            <Bug className="h-4 w-4" />
            Log Error Details
          </Button>
        )}
      </div>
      
      {error.digest && (
        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md">
          <p className="font-mono">Error ID: {error.digest}</p>
          <p className="mt-1">Please include this ID when contacting support.</p>
        </div>
      )}
    </div>
  )

  return (
    <ErrorPageTemplate
      {...ErrorTemplates.serverError}
      customActions={customActions}
      showBackButton={false}
    />
  )
} 